import { useMemo, useState } from "react";
import { useTable } from "react-table";
import {
  convertToTilteCase,
  defaultDateDisplay,
  formatCurrency,
} from "../../../helpers";
import primaryComponents from "../../primaryComponents";
import globalStyles from "../../../assets/styles/base/globalPage.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { pathConstants } from "../../../routes/pathContants";

const useReturnsTable = ({ returns = [] }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: ({ cell: { value } }) => defaultDateDisplay(value),
      },
      {
        Header: "Return Date & Time",
        accessor: "return_date",
        Cell: ({ cell: { value } }) => defaultDateDisplay(value),
      },
      {
        Header: "Order Number",
        accessor: "order",
        Cell: ({ cell: { value } }) => {
          const orderId = value?.id || value;
          return orderId ? (
            <Link
              className="smallest-text-size purple--text"
              to={`${pathConstants.INVENTORY_SALES_TRANSACTIONS}?orderId=${orderId}`}
              style={{ textDecoration: "underline" }}
            >
              #{orderId}
            </Link>
          ) : (
            "-"
          );
        },
      },
      {
        Header: "Return Amount",
        accessor: "total_requested_amount",
        Cell: ({ cell: { value }, row }) => (
          <span style={{ color: "#27AE60", fontWeight: 500 }}>
            {formatCurrency({
              value: value || row?.original?.total_returned_amount || 0,
              currencyCode: row?.original?.store?.currency_code,
            })}
          </span>
        ),
      },
      {
        Header: "Refunded Amount",
        accessor: "refundable_amount",
        Cell: ({ cell: { value }, row }) => {
          const returnRecord = row?.original;
          const baseAmount = Number(
            returnRecord?.total_requested_amount ??
              returnRecord?.total_returned_amount ??
              0,
          );
          const deliveryFee = Number(
            returnRecord?.total_requested_delivery_fee ??
              returnRecord?.total_returned_delivery_fee ??
              0,
          );
          const serviceCharge = Number(
            returnRecord?.total_requested_service_charge ??
              returnRecord?.total_returned_service_charge ??
              0,
          );

          return formatCurrency({
            value: value ?? baseAmount + deliveryFee + serviceCharge,
            currencyCode: returnRecord?.store?.currency_code,
          });
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => {
          const status = value || "REVIEW";
          const isPending =
            status === "REVIEW" ||
            status === "IN_REVIEW" ||
            status === "PENDING";
          const isApproved = status === "APPROVED";
          const isRejected = status === "REJECTED";

          const statusClass = isPending
            ? globalStyles["statusBlock--pending"]
            : isApproved
              ? globalStyles["statusBlock--success"]
              : isRejected
                ? globalStyles["statusBlock--failed"]
                : globalStyles.statusBlock;

          const displayText = isPending
            ? "In Review"
            : isApproved
              ? "Approved"
              : isRejected
                ? "Rejected"
                : status;

          return (
            <span
              className={`${globalStyles.statusBlock} ${statusClass}`}
              style={{ whiteSpace: "nowrap", fontSize: "11px" }}
            >
              {displayText}
            </span>
          );
        },
      },
      {
        Header: "Payment Method",
        accessor: "payment_method",
        Cell: ({ cell: { value } }) =>
          value ? convertToTilteCase(value.replace(/_/g, " ")) : "-",
      },
      {
        Header: "Cashier",
        accessor: "cashier",
        Cell: ({ cell: { value }, row }) =>
          value?.first_name || row?.original?.submitted_by?.first_name || "-",
      },
      {
        Header: "Customer",
        accessor: "customer",
        Cell: ({ cell: { value }, row }) => {
          const customer = value || row?.original?.order?.customer;
          const name = customer?.full_name;
          const id = customer?.id;

          if (id && name) {
            return (
              <Link
                className="smallest-text-size purple--text font-weight-normal"
                to={pathConstants.CUSTOMER_DETAILS({ customerId: id })}
                style={{ fontSize: "12px", textDecoration: "underline" }}
              >
                {name}
              </Link>
            );
          }
          return (
            <span style={{ fontSize: "12px" }}>
              {name || "Walk-In Customer"}
            </span>
          );
        },
      },
      {
        Header: "Store",
        accessor: "store",
        Cell: ({ cell: { value } }) => value?.name || "-",
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => {
          const status = row?.original?.status || "APPROVED";
          const isPending =
            status === "REVIEW" ||
            status === "IN_REVIEW" ||
            status === "PENDING";

          if (isPending) {
            return (
              <primaryComponents.Button
                classNames="btn btn--primary smallBtn"
                onClick={() => {
                  navigate(
                    pathConstants.INVENTORY_RETURNS_REVIEW({
                      id: row?.original?.id,
                    }),
                  );
                }}
              >
                Review
              </primaryComponents.Button>
            );
          }

          return (
            <primaryComponents.Button
              classNames="btn btn--primary smallBtn"
              onClick={() => {
                if (row?.original?.approved_return) {
                  setSelectedOrder(row?.original?.approved_return);
                } else if (status === "REJECTED") {
                  navigate(
                    pathConstants.INVENTORY_RETURNS_REVIEW({
                      id: row?.original?.id,
                    }),
                  );
                } else {
                  setSelectedOrder(row?.original?.id);
                }
              }}
            >
              View
            </primaryComponents.Button>
          );
        },
      },
    ],
    [navigate],
  );

  const instance = useTable({
    columns,
    data: returns || [],
  });

  return {
    instance,
    selectedOrder,
    setSelectedOrder,
  };
};

export default useReturnsTable;
